import { useState, useEffect } from "preact/hooks";
import type { TableRange, TablesConfig } from "@orderman/shared";
import { adminEvents, configFromFeed, adminDirty } from "../../state/adminData.js";
import { writeConfigSnapshot } from "../../state/store.js";

// Tischplan mobil: Nummern-Bereiche + Sonderplätze. Schreibt tables-config.snapshot. Ohne
// Konfiguration bleibt die Tisch-Eingabe frei (keine Einschränkung).

function TischeEditor({ initTables, initVersion }: { initTables: TablesConfig; initVersion: number }) {
  const [ranges, setRanges] = useState<TableRange[]>(() => initTables.ranges ?? []);
  const [extras, setExtras] = useState<string[]>(() => initTables.extras ?? []);
  const [version, setVersion] = useState(initVersion);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    adminDirty.value = false;
  }, []);
  const touch = () => (adminDirty.value = true);

  const patchRange = (i: number, p: Partial<TableRange>) => {
    touch();
    setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, ...p } : r)));
  };
  const addRange = () => {
    touch();
    setRanges((rs) => [...rs, { area: "", from: 1, to: 1 }]);
  };
  const delRange = (i: number) => {
    touch();
    setRanges((rs) => rs.filter((_, j) => j !== i));
  };

  const patchExtra = (i: number, v: string) => {
    touch();
    setExtras((es) => es.map((e, j) => (j === i ? v : e)));
  };
  const addExtra = () => {
    touch();
    setExtras((es) => [...es, ""]);
  };
  const delExtra = (i: number) => {
    touch();
    setExtras((es) => es.filter((_, j) => j !== i));
  };

  const save = () => {
    for (const r of ranges) {
      if (!r.area.trim()) return setMsg("Jeder Bereich braucht einen Namen.");
      if (r.to < r.from) return setMsg(`Bereich „${r.area}": bis < von.`);
    }
    const data: TablesConfig = {
      ranges: ranges.map((r) => ({ area: r.area.trim(), from: Number(r.from), to: Number(r.to) })),
      extras: extras.map((e) => e.trim()).filter((e) => e !== ""),
    };
    const next = version + 1;
    writeConfigSnapshot("tables", data, next);
    setVersion(next);
    adminDirty.value = false;
    setMsg("✓ Gespeichert – wird an alle Geräte verteilt.");
    setTimeout(() => setMsg(""), 2500);
  };

  return (
    <div class="admin-scroll">
      <h3 class="av-cat-title">Bereiche</h3>
      {ranges.map((r, i) => (
        <div key={i} class="tisch-range">
          <input
            class="tisch-area"
            placeholder="z. B. Zelt"
            value={r.area}
            onInput={(e) => patchRange(i, { area: e.currentTarget.value })}
          />
          <input
            class="tisch-num"
            type="number"
            inputMode="numeric"
            aria-label="von"
            value={r.from}
            onInput={(e) => patchRange(i, { from: Number(e.currentTarget.value) })}
          />
          <span class="tisch-dash">–</span>
          <input
            class="tisch-num"
            type="number"
            inputMode="numeric"
            aria-label="bis"
            value={r.to}
            onInput={(e) => patchRange(i, { to: Number(e.currentTarget.value) })}
          />
          <button class="pers-del" onClick={() => delRange(i)} aria-label="Entfernen">
            ✕
          </button>
        </div>
      ))}
      {ranges.length === 0 && <p class="admin-empty">Keine Bereiche — Tisch-Eingabe bleibt frei.</p>}
      <button class="pers-add" onClick={addRange}>
        + Bereich
      </button>

      <h3 class="av-cat-title" style="margin-top:18px">Sonderplätze</h3>
      {extras.map((e, i) => (
        <div key={i} class="tisch-range">
          <input
            class="tisch-area"
            placeholder="z. B. Außer Haus"
            value={e}
            onInput={(ev) => patchExtra(i, ev.currentTarget.value)}
          />
          <button class="pers-del" onClick={() => delExtra(i)} aria-label="Entfernen">
            ✕
          </button>
        </div>
      ))}
      {extras.length === 0 && <p class="admin-empty">Keine Sonderplätze.</p>}
      <button class="pers-add" onClick={addExtra}>
        + Sonderplatz
      </button>

      {msg && <div class="admin-flash">{msg}</div>}

      <div class="av-savebar">
        <button class="btn-primary" onClick={save}>
          Speichern
        </button>
      </div>
    </div>
  );
}

export function TischeView() {
  if (adminEvents.value === null) return <div class="admin-empty">Lade Tische…</div>;
  const cfg = configFromFeed(adminEvents.value);
  return <TischeEditor initTables={cfg.tables} initVersion={cfg.tablesVersion} />;
}
